import {
  browserTimezone,
  formatTime,
  isPast,
  isTwoDayTimeRange,
  isWithinHours,
  nowInTimezone,
  parseTime,
  shortenTimezone,
} from "@gigpro/utils/datetime";
import type { GigApplication } from "api/resources/gigApplications";
import type { Gig } from "api/resources/gigs";
import type { PaymentStatus } from "api/resources/Payment";
import type { GigType, GigCancellationReason } from "api/resources/shared";
import type { ConfirmedPro, StafferGig } from "api/resources/StafferGig";
import { addMinutes, areIntervalsOverlapping, differenceInHours, differenceInMinutes, isSameDay } from "date-fns";

export type TeamCaptainRole = "TEAM_CAPTAIN" | "EVENT_LEAD";

type IsTeamCaptainParams = { gig: StafferGig } | { skill: string | undefined };
const skillOf = (params: IsTeamCaptainParams) => ("skill" in params ? params.skill : params.gig.skill);

export const isTeamCaptain = (params: IsTeamCaptainParams): boolean =>
  skillOf(params) === "Team Captain" || skillOf(params) === "Event Lead";

export const isAutoSelectExcludedPosition = (params: IsTeamCaptainParams): boolean =>
  skillOf(params) === "Team Captain";

export const getTeamCaptainLabel = (params: IsTeamCaptainParams): string =>
  skillOf(params) === "Event Lead" ? "Event Lead" : "Team Captain";

export const getTeamCaptainRole = (params: IsTeamCaptainParams): TeamCaptainRole =>
  skillOf(params) === "Event Lead" ? "EVENT_LEAD" : "TEAM_CAPTAIN";

export function getConfirmedPro({ gigster, ...application }: GigApplication): ConfirmedPro {
  return {
    id: gigster.id,
    applicationId: application.id,
    applicationReview: application.review,
    headshot_public_id: gigster.gigster_profile?.headshot_public_id,
    fullName: `${gigster.first_name} ${gigster.last_name}`,
    first_name: gigster.first_name,
    last_name: gigster.last_name,
    reviewCount: gigster.review_count,
    rating: gigster.rating,
    is_blocked: !!application.is_blocked,
    is_favorite: !!application.is_favorite,
    working_hours: application.working_hours,
    payment: application.payment,
    review: application.review,
  };
}

const RATING_CUTOFF_HOURS = 24;
/**
 * `reason` is whatever the valid-reasons endpoint offered, so it's typed as a string rather than a
 * locally-held enum. Only the two legacy pro-fault reasons below carry a suggested rating; every
 * other value — known or not yet known to the client — falls through to no default.
 */
export function getDefaultCancellationRating(gig: StafferGig, reason: string) {
  if (reason === "NO_SHOW") {
    return {
      defaultRating: 1,
      defaultRatingMessage:
        "We suggest a rating of 1 star when a Pro does not show up for a gig. You are welcome to change this rating if you would like.",
    };
  }
  if (reason === "CALLED_OUT") {
    const hoursBeforeGigStart = differenceInHours(gig.start_time, nowInTimezone(gig.location.timezone));
    if (hoursBeforeGigStart < RATING_CUTOFF_HOURS) {
      return {
        defaultRating: 2,
        defaultRatingMessage:
          "We suggest a rating of 2 stars when a Pro calls out less than 24 hours before the gig starts. You are welcome to change this rating if you would like.",
      };
    }
  }
  return {};
}

export function isGigFilled(gig: StafferGig): boolean {
  return gig.stats.confirmed_pros_count === gig.allowed_confirmed_pros;
}

export function isGigStarted(gig: StafferGig | Gig): boolean {
  return isPast(gig.start_time, gig.location.timezone);
}

export function isGigEnded(gig: StafferGig | Gig): boolean {
  return isPast(gig.end_time, gig.location.timezone);
}

export function isShiftToday(gig: StafferGig | Gig): boolean {
  return isSameDay(gig.start_time, nowInTimezone(gig.location.timezone));
}

/**
 * 90-min lead lets managers prep before start; 30-min tail covers clock-out fixes after end.
 * `isShiftLive` and the stats query share it, or the card vanishes while the endpoint still
 * reports the shift active.
 */
export const ACTIVE_SHIFT_WINDOW = { beforeMinutes: 90, afterMinutes: 30 };

function isWithinActiveWindow(gig: StafferGig | Gig): boolean {
  const tail = addMinutes(gig.end_time, ACTIVE_SHIFT_WINDOW.afterMinutes);
  return isGigStarted(gig) && !isPast(tail, gig.location.timezone);
}

/** Shift is today, still running, or inside the clock-out tail — an overnight one stays live. */
export function isShiftLive(gig: StafferGig | Gig): boolean {
  return isShiftToday(gig) || isWithinActiveWindow(gig);
}

const PAID_MAP = new Set<PaymentStatus>(["AVAILABLE", "PENDING_TRANSFER", "IN_TRANSIT", "SUBMITTED"]);
export function isShiftPaid(application: GigApplication): boolean {
  return !!application.payment?.status && PAID_MAP.has(application.payment?.status);
}

const omittedReasons = new Set<GigCancellationReason>(["CALLED_OUT", "NO_SHOW", "OTHER", "PRO_ISSUE"]);
export function isGigWithin4hPaymentPolicy(gig: StafferGig, cancellationReason: GigCancellationReason): boolean {
  return (
    gig.stats.confirmed_pros_count > 0 &&
    isWithinHours(gig.start_time, 24, gig.location.timezone) &&
    !isGigStarted(gig) &&
    !omittedReasons.has(cancellationReason)
  );
}

type GigTimes = { start_time: Date; end_time: Date };
export const areGigsOverlapping = (gig: GigTimes, compareGig: GigTimes): boolean =>
  areIntervalsOverlapping(
    { start: gig.start_time, end: gig.end_time },
    { start: compareGig.start_time, end: compareGig.end_time },
  );

export const isGigPastHours = (gig: StafferGig, hours: number) => {
  const end = gig.end_time;
  const now = nowInTimezone(gig.location.timezone);
  const diff = differenceInHours(now, end);
  return diff > hours;
};

type GetTimeStringProps =
  | {
      start_time: Date | string;
      end_time: Date | string;
      timezone: string;
    }
  | Pick<StafferGig, "start_time" | "end_time" | "location">;
export function getTimeString(params: GetTimeStringProps, options?: { plusOneDay?: boolean; at?: Date }): string {
  const { plusOneDay = true, at } = options ?? {};
  const startTimeDate = typeof params.start_time === "string" ? parseTime(params.start_time) : params.start_time;
  const endTimeDate = typeof params.end_time === "string" ? parseTime(params.end_time) : params.end_time;

  const timezone = ("timezone" in params ? params.timezone : params.location.timezone) ?? browserTimezone;
  return `${formatTime(startTimeDate)} - ${formatTime(endTimeDate)} (${shortenTimezone(timezone, at)})${plusOneDay && isTwoDayTimeRange(startTimeDate, endTimeDate) ? " (+1 day)" : ""}`;
}

export function canManageTimeAndAttendance(gig: StafferGig): boolean {
  return gig.stats.confirmed_pros_count > 0 && gig.status !== "COMPLETED";
}

type CommonGig = { event?: { id: string } | null; type: GigType; location_id: number };
export function getEventRoute(gig: CommonGig): string {
  if (!gig.event) return "";
  const tab = gig.type === "NETWORK" ? "network" : "marketplace";
  return `/locations/${gig.location_id}/event/${gig.event.id}/${tab}`;
}

export function getClockStatus(gig: StafferGig, application: GigApplication) {
  const nowInGigTime = nowInTimezone(gig.location.timezone);
  const hasClockedIn = application.timesheet?.time_punches?.some((p) => p.punch_type === "CLOCK_IN") ?? false;
  const hasClockedOut = application.timesheet?.time_punches?.some((p) => p.punch_type === "CLOCK_OUT") ?? false;
  const canClockIn =
    !hasClockedIn &&
    differenceInMinutes(gig.start_time, nowInGigTime) <= 90 &&
    !isPast(gig.end_time, gig.location.timezone);
  const canClockOut = hasClockedIn && !hasClockedOut && differenceInMinutes(nowInGigTime, gig.end_time) <= 90;
  return { hasClockedIn, hasClockedOut, canClock: canClockIn || canClockOut };
}
